/**
 * "Käyttäjähallinta" — single admin modal with 6 tabs.
 *
 * Direct port of the mockup `#adminPanel` overlay
 * (`vertailutyokalu.html:3915+`, `openAdminPanel` at line 6112). One overlay
 * holds all admin tooling; each tab calls into an existing renderer:
 *
 *   - **Käyttäjät**     → `renderAdminUsersInto`      (M2 user list + actions)
 *   - **Uusi käyttäjä** → inline form, calls `adminCreateUser`
 *   - **Jaetut tilat**  → `renderAdminSharesInto`     (M5 admin shares)
 *   - **Tilaukset**     → `renderAdminOrdersInto`     (M5 order queue)
 *   - **Palaute**       → `renderAdminFeedbackInto`   (M5 feedback queue)
 *   - **Tapahtumat**    → `renderAdminAuditInto`      (M5 audit log)
 *
 * Badges on the Käyttäjät / Tilaukset / Palaute tabs show pending counts.
 * Routing: every legacy `#/admin/*` URL opens this modal — see `main.ts`.
 */
import { listUsers, adminListShares, adminCreateUser, type AdminCreateUserInput } from '../api/admin.ts';
import { adminListOrders } from '../api/orders.ts';
import { adminListFeedback } from '../api/feedback.ts';
import { APIRequestError } from '../api/client.ts';
import { currentUser } from '../state/session.ts';
import { REGIONS } from '../data/regions.ts';
import { renderAdminUsers } from './admin-users.ts';
import { renderAdminOrders } from './admin-orders.ts';
import { renderAdminFeedback } from './admin-feedback.ts';
import { renderAdminAudit } from './admin-audit.ts';
import { renderAdminAnalysisPrompt } from './admin-analysis-prompt.ts';
import { t, getLang } from '../i18n/index.ts';
import type { Share } from '../api/shares.ts';
import type { User } from '../types/api.ts';

type TabName = 'users' | 'create' | 'shares' | 'orders' | 'feedback' | 'log' | 'prompt';

const TABS: { id: TabName; key: string }[] = [
  { id: 'users', key: 'admin_tab_users' },
  { id: 'create', key: 'admin_tab_create' },
  { id: 'shares', key: 'admin_tab_shares' },
  { id: 'orders', key: 'admin_tab_orders' },
  { id: 'feedback', key: 'admin_tab_feedback' },
  { id: 'log', key: 'admin_tab_log' },
  { id: 'prompt', key: 'admin_tab_prompt' },
];

let activeTab: TabName = 'users';

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** Open the modal, switching to `initialTab` if given. */
export function openAdminPanel(initialTab?: TabName): void {
  const u = currentUser();
  if (!u || u.role !== 'admin') return;
  activeTab = initialTab ?? activeTab;

  let overlay = document.getElementById('adminPanel') as HTMLDivElement | null;
  if (!overlay) {
    overlay = document.createElement('div');
    overlay.id = 'adminPanel';
    overlay.className = 'admin-overlay';
    document.body.appendChild(overlay);
  }
  overlay.innerHTML = modalShell();
  overlay.style.display = 'flex';

  // Bind close button + backdrop click
  overlay.querySelector<HTMLButtonElement>('.admin-close')!.addEventListener('click', closeAdminPanel);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) closeAdminPanel();
  });

  // Tab buttons
  overlay.querySelectorAll<HTMLButtonElement>('.admin-tab').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.dataset.tab as TabName | undefined;
      if (!id || id === activeTab) return;
      activeTab = id;
      switchTab();
    });
  });

  // Initial tab paint + badge refresh
  switchTab();
  void refreshBadges();
}

export function closeAdminPanel(): void {
  const overlay = document.getElementById('adminPanel');
  if (overlay) overlay.style.display = 'none';
}

// ---------------------------------------------------------------------------
// Modal HTML
// ---------------------------------------------------------------------------

function modalShell(): string {
  const tabs = TABS.map(
    (tab) => `
      <button class="admin-tab ${tab.id === activeTab ? 'active' : ''}" data-tab="${tab.id}">
        ${escapeHtml(t(tab.key))}${badgeFor(tab.id)}
      </button>`,
  ).join('');

  return `
    <div class="admin-card">
      <div class="admin-header">
        <div>
          <div class="admin-title">${escapeHtml(t('view_admin_title'))}</div>
          <div class="admin-sub">${escapeHtml(t('view_admin_sub'))}</div>
        </div>
        <button class="admin-close" title="${escapeHtml(t('close'))}" aria-label="${escapeHtml(t('close'))}">×</button>
      </div>
      <div class="admin-tabs">${tabs}</div>
      <div class="admin-tab-content" id="adminTabBody"></div>
    </div>
  `;
}

function badgeFor(id: TabName): string {
  const badgeIds: Partial<Record<TabName, string>> = {
    users: 'pendingUsersBadge',
    orders: 'ordersBadge',
    feedback: 'feedbackBadge',
  };
  const bId = badgeIds[id];
  if (!bId) return '';
  return ` <span id="${bId}" class="admin-tab-badge" style="display:none">0</span>`;
}

// ---------------------------------------------------------------------------
// Tab routing
// ---------------------------------------------------------------------------

function switchTab(): void {
  document.querySelectorAll<HTMLButtonElement>('#adminPanel .admin-tab').forEach((b) => {
    b.classList.toggle('active', b.dataset.tab === activeTab);
  });
  const body = document.getElementById('adminTabBody') as HTMLDivElement | null;
  if (!body) return;
  body.innerHTML = `<div class="placeholder-card">${escapeHtml(t('view_admin_loading'))}</div>`;

  switch (activeTab) {
    case 'users':
      void renderAdminUsers(body);
      break;
    case 'create':
      renderCreateUserTab(body);
      break;
    case 'shares':
      void renderSharesTab(body);
      break;
    case 'orders':
      void renderAdminOrders(body);
      break;
    case 'feedback':
      void renderAdminFeedback(body);
      break;
    case 'log':
      void renderAdminAudit(body);
      break;
    case 'prompt':
      void renderAdminAnalysisPrompt(body);
      break;
  }
}

// ---------------------------------------------------------------------------
// Badges — pending counts shown on the Users / Orders / Feedback tabs.
// ---------------------------------------------------------------------------

async function refreshBadges(): Promise<void> {
  await Promise.all([
    listUsers({ status: 'pending' }).then((rows) => setBadge('pendingUsersBadge', rows.length)).catch(() => {}),
    adminListOrders({ status: 'pending' }).then((rows) => setBadge('ordersBadge', rows.length)).catch(() => {}),
    adminListFeedback('new').then((rows) => setBadge('feedbackBadge', rows.length)).catch(() => {}),
  ]);
}

function setBadge(id: string, n: number): void {
  const el = document.getElementById(id);
  if (!el) return;
  if (n > 0) {
    el.textContent = String(n);
    el.style.display = '';
  } else {
    el.style.display = 'none';
  }
}

// ---------------------------------------------------------------------------
// "Uusi käyttäjä" — admin-create form (Register → Approve under the hood).
// ---------------------------------------------------------------------------

function renderCreateUserTab(host: HTMLElement): void {
  host.innerHTML = `
    <form class="admin-form" id="adminCreateForm" novalidate>
      <div class="form-row">
        <div class="form-field">
          <label class="form-label">${escapeHtml(t('admin_create_first_name'))}</label>
          <input class="admin-input" name="first_name" required minlength="1">
        </div>
        <div class="form-field">
          <label class="form-label">${escapeHtml(t('admin_create_last_name'))}</label>
          <input class="admin-input" name="last_name" required minlength="1">
        </div>
      </div>
      <div class="form-row">
        <div class="form-field">
          <label class="form-label">${escapeHtml(t('admin_create_email'))}</label>
          <input type="email" class="admin-input" name="email" required>
        </div>
        <div class="form-field">
          <label class="form-label">${escapeHtml(t('admin_create_password'))}</label>
          <input type="password" class="admin-input" name="password" required minlength="8">
        </div>
      </div>
      <div class="form-row">
        <div class="form-field">
          <label class="form-label">${escapeHtml(t('admin_create_organization'))}</label>
          <input class="admin-input" name="organization">
        </div>
        <div class="form-field">
          <label class="form-label">${escapeHtml(t('admin_create_role'))}</label>
          <select class="admin-input" name="role">
            <option value="user">user</option>
            <option value="institution">institution</option>
            <option value="admin">admin</option>
          </select>
        </div>
        <div class="form-field">
          <label class="form-label">${escapeHtml(t('admin_create_level'))}</label>
          <select class="admin-input" name="level">
            <option value="1">${escapeHtml(t('level_1'))}</option>
            <option value="2" selected>${escapeHtml(t('level_2'))}</option>
            <option value="3">${escapeHtml(t('level_3'))}</option>
            <option value="4">${escapeHtml(t('level_4'))}</option>
          </select>
        </div>
        <div class="form-field form-field-submit">
          <label class="form-label">&nbsp;</label>
          <button type="submit" class="admin-action-btn admin-action-primary admin-create-submit-btn">${escapeHtml(t('admin_create_submit'))}</button>
        </div>
      </div>
      <div class="form-row" id="regionsRow" style="display:none">
        <div class="form-field" style="flex:1">
          <label class="form-label">${escapeHtml(t('admin_create_regions'))}</label>
          <div class="region-checklist">
            ${REGIONS.map(
              (r) =>
                `<label class="region-check-item">
                  <input type="checkbox" name="allowed_regions" value="${escapeHtml(r.name)}">
                  <span>${escapeHtml(r.name)}</span>
                </label>`,
            ).join('')}
          </div>
        </div>
      </div>
      <div class="modal-msg" id="adminCreateMsg" style="margin-top:8px"></div>
    </form>
  `;

  const form = host.querySelector<HTMLFormElement>('#adminCreateForm')!;
  const levelSel = form.querySelector<HTMLSelectElement>('select[name=level]')!;
  const regionsRow = form.querySelector<HTMLDivElement>('#regionsRow')!;
  // Region-checklist näytetään "Taso 1" -käyttäjille (DB level=1, joka on
  // BE:n maakuntakohtainen-tila). FE i18n korjattu 2026-05-31 niin että
  // dropdown-arvo === '1' tarkoittaa nyt nimenomaan tätä tasoa; aiempi
  // off-by-one (label "Taso 1" = level=0) on dokumentoitu migraatio 023:n
  // header-kommentissa.
  const refreshRegions = (): void => {
    regionsRow.style.display = levelSel.value === '1' ? '' : 'none';
  };
  levelSel.addEventListener('change', refreshRegions);
  refreshRegions();

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const msg = form.querySelector<HTMLDivElement>('#adminCreateMsg')!;
    msg.className = 'modal-msg';
    msg.textContent = '';
    const data = new FormData(form);
    const regions = Array.from(form.querySelectorAll<HTMLInputElement>('input[name=allowed_regions]:checked')).map(
      (c) => c.value,
    );
    const input: AdminCreateUserInput = {
      email: String(data.get('email') || '').trim(),
      password: String(data.get('password') || ''),
      first_name: String(data.get('first_name') || '').trim(),
      last_name: String(data.get('last_name') || '').trim(),
      organization: String(data.get('organization') || '').trim(),
      role: data.get('role') as AdminCreateUserInput['role'],
      level: Number(data.get('level') || 2),
      allowed_regions: regions,
    };
    const btn = form.querySelector<HTMLButtonElement>('button[type=submit]')!;
    btn.disabled = true;
    try {
      const created: User = await adminCreateUser(input);
      void refreshBadges();
      // Replace the form with a confirmation card.
      const levelLabel = t(`level_${Math.max(0, Math.min(created.level || input.level, 4))}`);
      host.innerHTML = `
        <div class="admin-create-confirm">
          <div class="admin-create-confirm-icon">✓</div>
          <div class="admin-create-confirm-title">${escapeHtml(t('admin_create_ok', { email: created.email }))}</div>
          <div class="admin-create-confirm-details">
            <span><strong>${escapeHtml(t('admin_create_ok_name'))}:</strong> ${escapeHtml(created.name || `${input.first_name} ${input.last_name}`.trim())}</span>
            <span><strong>${escapeHtml(t('admin_create_ok_role'))}:</strong> ${escapeHtml(created.role || input.role)}</span>
            <span><strong>${escapeHtml(t('admin_create_ok_level'))}:</strong> ${escapeHtml(levelLabel)}</span>
          </div>
          <button class="admin-action-btn admin-action-primary" id="adminCreateAgainBtn">${escapeHtml(t('admin_create_another'))}</button>
        </div>
      `;
      host.querySelector('#adminCreateAgainBtn')!.addEventListener('click', () => renderCreateUserTab(host));
    } catch (err) {
      msg.classList.add('err');
      msg.textContent = formatErr(err);
      btn.disabled = false;
    }
  });
}

// ---------------------------------------------------------------------------
// "Jaetut tilat" — admin global shares list.
// ---------------------------------------------------------------------------

async function renderSharesTab(host: HTMLElement): Promise<void> {
  host.innerHTML = `
    <div class="admin-filters">
      <input class="admin-input" id="shareSearch" placeholder="${escapeHtml(t('admin_shares_search_ph'))}">
      <select class="admin-input" id="shareFilterStatus" style="max-width:200px">
        <option value="active">${escapeHtml(t('admin_shares_filter_active'))}</option>
        <option value="revoked">${escapeHtml(t('admin_shares_filter_revoked'))}</option>
        <option value="all">${escapeHtml(t('admin_shares_filter_all'))}</option>
      </select>
    </div>
    <div id="sharesListContainer"><div class="placeholder-card">${escapeHtml(t('view_admin_loading'))}</div></div>
  `;
  const search = host.querySelector<HTMLInputElement>('#shareSearch')!;
  const statusSel = host.querySelector<HTMLSelectElement>('#shareFilterStatus')!;
  const list = host.querySelector<HTMLDivElement>('#sharesListContainer')!;
  let cache: Share[] = [];

  const reload = async (): Promise<void> => {
    list.innerHTML = `<div class="placeholder-card">${escapeHtml(t('view_admin_loading'))}</div>`;
    try {
      cache = await adminListShares(statusSel.value as 'active' | 'revoked' | 'all');
      paint();
    } catch (err) {
      list.innerHTML = `<div class="placeholder-card" style="color:rgb(140,55,55)">${escapeHtml(formatErr(err))}</div>`;
    }
  };
  const paint = (): void => {
    const q = search.value.trim().toLowerCase();
    const rows = q
      ? cache.filter(
          (s) =>
            (s.sharer_name || '').toLowerCase().includes(q) ||
            (s.sharer_email || '').toLowerCase().includes(q) ||
            (s.recipient_email || '').toLowerCase().includes(q) ||
            (s.recipient_name || '').toLowerCase().includes(q) ||
            (s.estate_name || '').toLowerCase().includes(q) ||
            String(s.estate_id).includes(q),
        )
      : cache;
    if (!rows.length) {
      list.innerHTML = `<div class="placeholder-card">${escapeHtml(t('admin_shares_empty'))}</div>`;
      return;
    }
    list.innerHTML = `
      <div class="listing-table-wrap"><table class="listing-table">
        <thead><tr>
          <th>${escapeHtml(t('admin_shares_th_created'))}</th>
          <th>${escapeHtml(t('admin_shares_th_estate'))}</th>
          <th>${escapeHtml(t('admin_shares_th_sharer'))}</th>
          <th>${escapeHtml(t('admin_shares_th_recipient'))}</th>
          <th>${escapeHtml(t('admin_shares_th_status'))}</th>
          <th>${escapeHtml(t('admin_shares_th_message'))}</th>
        </tr></thead>
        <tbody>
          ${rows
            .map(
              (s) => `<tr>
                <td>${escapeHtml(formatDate(s.created_at))}</td>
                <td>${escapeHtml(s.estate_name || `#${s.estate_id}`)}<br><span style="opacity:.7">${escapeHtml(s.municipality || '')}${s.estate_region ? ', ' + escapeHtml(s.estate_region) : ''}</span></td>
                <td>${escapeHtml(s.sharer_name || '')}<br><span style="opacity:.7">${escapeHtml(s.sharer_email || '')}</span></td>
                <td>${escapeHtml(s.recipient_name || '')}<br><span style="opacity:.7">${escapeHtml(s.recipient_email || '')}</span></td>
                <td>${s.revoked_at ? `<span class="user-status-pill user-status-rejected">${escapeHtml(t('admin_shares_revoked'))}</span>` : `<span class="user-status-pill user-status-active">${escapeHtml(t('admin_shares_active'))}</span>`}</td>
                <td>${escapeHtml(s.message || '')}</td>
              </tr>`,
            )
            .join('')}
        </tbody>
      </table></div>
    `;
  };

  search.addEventListener('input', paint);
  statusSel.addEventListener('change', () => void reload());
  await reload();
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatErr(e: unknown): string {
  if (e instanceof APIRequestError) return e.body?.error || e.message;
  if (e instanceof Error) return e.message;
  return String(e);
}
function formatDate(s: string | undefined | null): string {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleString(getLang() === 'en' ? 'en-GB' : 'fi-FI');
  } catch {
    return s;
  }
}
function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  }[c]!));
}
